import React, { useState, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Input,
  Button,
  Space,
  message,
  Typography,
  Upload,
  theme,
  Dropdown,
  Modal,
  Form,
  DatePicker,
  Tag,
} from 'antd';
import {
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  CommentOutlined,
  EyeOutlined,
  EnterOutlined,
  SyncOutlined,
  FormOutlined,
  CheckCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { WORKSPACE_EVENT_PORTAL_ACTIONS } from './workspaceEventConstants';

const { Text } = Typography;

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

const filesToUploadFileList = (fileArray) =>
  fileArray.map((file, index) => ({
    uid: `${file.name}-${index}-${file.lastModified}`,
    name: file.name,
    status: 'done',
    originFileObj: file,
  }));

const PORTAL_MENU = [
  { key: WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION, label: 'Request a revision' },
  { key: WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT, label: 'Request a document' },
  { key: WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_CONFIRMATION, label: 'Request confirmation' },
];

const modalTitleForAction = (action) => {
  const item = PORTAL_MENU.find((i) => i.key === action);
  return item ? item.label : 'Request';
};

/** Actions that need a modal to collect extra details before attaching to the composer. */
const PORTAL_ACTIONS_NEED_MODAL = new Set([
  WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION,
  WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT,
]);

function portalPendingIcon(action) {
  if (action === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION) return <SyncOutlined />;
  if (action === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT) return <FormOutlined />;
  return <CheckCircleOutlined />;
}

function portalPendingLabel(action, meta) {
  if (action === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION) {
    return meta?.due_at
      ? `Request revision · Due ${dayjs(meta.due_at).format('MMM D, YYYY')}`
      : 'Request a revision';
  }
  if (action === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT) {
    return meta?.document_label ? `Request document: ${meta.document_label}` : 'Request a document';
  }
  if (action === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_CONFIRMATION) return 'Request confirmation';
  return 'Request';
}

const WorkspaceEventComposer = forwardRef(({
  canComment,
  canUpload,
  portalHandlers,
  onSubmitted,
  layout = 'default',
  replyContext = null,
  onClearReplyContext = null,
  lockedReason = null,
}, ref) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingMeta, setPendingMeta] = useState(null);
  const textareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focusTextarea: () => { textareaRef.current?.focus(); },
  }), []);

  const isRail = layout === 'rail';
  const showRequests = Boolean(portalHandlers?.postWorkspaceEvent);

  const workspaceUploadStyles = useMemo(
    () => ({
      root: {
        borderRadius: token.borderRadius,
      },
      item: {
        borderRadius: token.borderRadiusSM,
        backgroundColor: token.colorFillTertiary,
      },
      trigger: {
        backgroundColor: token.colorPrimaryBg,
        padding: token.paddingSM,
        borderRadius: token.borderRadiusSM,
      },
    }),
    [token],
  );

  const uploadFileList = useMemo(() => filesToUploadFileList(files), [files]);

  const handleUploadChange = useCallback(({ fileList }) => {
    const next = fileList
      .filter((item) => item.originFileObj instanceof File)
      .map((item) => item.originFileObj);
    setFiles(next);
  }, []);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed && !files.length && !pendingAction) {
      message.warning(
        showRequests
          ? 'Add a message, attach files, or select a request type.'
          : 'Add a message or attach files',
      );
      return;
    }
    setSubmitting(true);
    try {
      if (replyContext && portalHandlers?.postWorkspaceEventReply) {
        await portalHandlers.postWorkspaceEventReply({
          workspaceEventPublicId: replyContext.workspaceEventPublicId,
          body: trimmed,
          files,
        });
        onClearReplyContext?.();
      } else {
        if (!portalHandlers?.postWorkspaceEvent) {
          message.error('Workspace is not available');
          return;
        }
        const formData = new FormData();
        if (trimmed) formData.append('body', trimmed);
        files.forEach((f) => formData.append('files[]', f));
        if (pendingAction) {
          formData.append('action', pendingAction);
          if (pendingMeta && Object.keys(pendingMeta).length > 0) {
            formData.append('metadata', JSON.stringify(pendingMeta));
          }
        }
        await portalHandlers.postWorkspaceEvent(formData);
      }
      message.success('Posted');
      setText('');
      setFiles([]);
      setPendingAction(null);
      setPendingMeta(null);
      onSubmitted?.();
    } catch (e) {
      const serverMessage = e?.response?.data?.message;
      message.error(serverMessage || 'Could not post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (action) => {
    if (isRail && !PORTAL_ACTIONS_NEED_MODAL.has(action)) {
      // No details needed — attach the request directly to the composer, no modal.
      setPendingAction(action);
      setPendingMeta({});
      return;
    }
    setPendingAction(action);
    setPendingMeta(null);
    form.resetFields();
    setActionModalOpen(true);
  };

  const handleModalApply = async () => {
    try {
      const values = await form.validateFields();
      const meta = {};
      if (values.due_at) meta.due_at = dayjs(values.due_at).format('YYYY-MM-DD');
      if (values.document_label) meta.document_label = values.document_label;
      setPendingMeta(meta);
      setActionModalOpen(false);
      form.resetFields();
    } catch (e) {
      if (e?.errorFields) return;
    }
  };

  const submitStructuredRequest = async () => {
    if (!pendingAction || !portalHandlers?.postWorkspaceEvent) return;
    try {
      const values = await form.validateFields();
      const meta = {};
      if (values.note) meta.note = values.note;
      if (pendingAction === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION && values.due_at) {
        meta.due_at = dayjs(values.due_at).format('YYYY-MM-DD');
      }
      if (pendingAction === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT && values.document_label) {
        meta.document_label = values.document_label;
      }
      const formData = new FormData();
      formData.append('action', pendingAction);
      if (Object.keys(meta).length > 0) formData.append('metadata', JSON.stringify(meta));
      setSubmitting(true);
      await portalHandlers.postWorkspaceEvent(formData);
      message.success('Sent');
      setActionModalOpen(false);
      setPendingAction(null);
      setPendingMeta(null);
      form.resetFields();
      onSubmitted?.();
    } catch (e) {
      if (e?.errorFields) return;
      const serverMessage = e?.response?.data?.message;
      message.error(serverMessage || 'Could not send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canComment && !canUpload && !showRequests) {
    return null;
  }

  if (lockedReason) {
    const isRailLocked = layout === 'rail';
    return (
      <div
        className={
          isRailLocked
            ? 'workspace-inline-composer workspace-inline-composer--locked'
            : undefined
        }
        style={
          isRailLocked
            ? undefined
            : {
                borderTop: '1px solid var(--varexe-border-color)',
                paddingTop: 'var(--ant-padding-sm)',
              }
        }
      >
        <Space align="start" size="small" style={{ padding: token.paddingSM }}>
          <LockOutlined style={{ color: token.colorTextTertiary, marginTop: 2 }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {lockedReason}
          </Text>
        </Space>
      </div>
    );
  }

  const structuredModal = showRequests ? (
    <Modal
      title={modalTitleForAction(pendingAction)}
      open={actionModalOpen}
      onCancel={() => {
        setActionModalOpen(false);
        setPendingAction(null);
        setPendingMeta(null);
        form.resetFields();
      }}
      onOk={() => isRail ? void handleModalApply() : void submitStructuredRequest()}
      okText={isRail ? 'Apply' : 'Send'}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: token.marginMD }}>
        {pendingAction === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_REVISION ? (
          <Form.Item name="due_at" label="Preferred due date (optional)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        ) : null}
        {pendingAction === WORKSPACE_EVENT_PORTAL_ACTIONS.REQUEST_DOCUMENT ? (
          <Form.Item
            name="document_label"
            label="What do you need?"
            rules={[{ required: true, message: 'Describe the document' }]}
          >
            <Input placeholder="e.g. Updated quote" />
          </Form.Item>
        ) : null}
        {!isRail && (
          <Form.Item name="note" label="Message (optional)">
            <Input.TextArea rows={3} placeholder="Add details for the business" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  ) : null;

  // ── Rail layout: rich inline docked composer ─────────────────────────────
  if (isRail) {
    return (
      <>
        {structuredModal}
        <div className="workspace-inline-composer">
          <div className="workspace-inline-composer__header">
            <div className="workspace-inline-composer__tabs">
              <button
                type="button"
                className="workspace-inline-composer__tab workspace-inline-composer__tab--active"
              >
                <CommentOutlined />
                {replyContext ? 'Reply' : 'Message'}
              </button>
            </div>
            <Text type="secondary" className="workspace-inline-composer__visibility">
              <EyeOutlined /> Visible to team
            </Text>
          </div>

          <div className="workspace-inline-composer__body">
            {canComment && (
              <Input.TextArea
                ref={textareaRef}
                variant="borderless"
                autoSize={{ minRows: 2, maxRows: 6 }}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a reply, or attach a file…"
                disabled={submitting}
                className="workspace-inline-composer__textarea"
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            )}
            {replyContext && (
              <div className="workspace-inline-composer__reply-context">
                <Tag
                  icon={<EnterOutlined />}
                  color="default"
                  closable
                  onClose={(e) => { e.preventDefault(); onClearReplyContext?.(); }}
                >
                  Replying to {replyContext.actorName}
                </Tag>
              </div>
            )}
            {pendingAction && pendingMeta !== null && !actionModalOpen && (
              <div className="workspace-inline-composer__pending">
                <Tag
                  icon={portalPendingIcon(pendingAction)}
                  color="blue"
                  closable
                  onClose={(e) => { e.preventDefault(); setPendingAction(null); setPendingMeta(null); }}
                >
                  {portalPendingLabel(pendingAction, pendingMeta)}
                </Tag>
              </div>
            )}
            {files.length > 0 && (
              <div className="workspace-inline-composer__file-list">
                <Upload
                  multiple
                  fileList={uploadFileList}
                  beforeUpload={() => false}
                  onChange={handleUploadChange}
                  disabled={submitting}
                  showUploadList={{ showRemoveIcon: true }}
                  openFileDialogOnClick={false}
                  styles={workspaceUploadStyles}
                />
              </div>
            )}
          </div>

          <div className="workspace-inline-composer__footer">
            <Space align="center" size={4}>
              {canUpload && (
                <Upload
                  multiple
                  fileList={uploadFileList}
                  beforeUpload={() => false}
                  onChange={handleUploadChange}
                  disabled={submitting}
                  showUploadList={false}
                >
                  <button
                    type="button"
                    className="workspace-inline-composer__icon-btn"
                    disabled={submitting}
                    title="Attach files"
                  >
                    <PaperClipOutlined />
                    {files.length > 0 && (
                      <span className="workspace-inline-composer__file-badge">{files.length}</span>
                    )}
                  </button>
                </Upload>
              )}
            </Space>
            <Space align="center" size="small">
              <Text type="secondary" className="workspace-inline-composer__hint">
                {isMac ? '⌘' : 'Ctrl'} ↵ to send
              </Text>
              {showRequests && (
                <Dropdown
                  menu={{ items: PORTAL_MENU, onClick: ({ key }) => openActionModal(key) }}
                  trigger={['click']}
                  disabled={Boolean(replyContext)}
                >
                  <Button size="small" icon={<PlusOutlined />} disabled={submitting || Boolean(replyContext)}>
                    Request
                  </Button>
                </Dropdown>
              )}
              {(canComment || canUpload) && (
                <Button
                  type="primary"
                  size="small"
                  icon={<SendOutlined />}
                  loading={submitting}
                  onClick={() => void handleSubmit()}
                >
                  Send
                </Button>
              )}
            </Space>
          </div>
        </div>
      </>
    );
  }

  // ── Default (card embed) layout ──────────────────────────────────────────
  const uploadControl = canUpload ? (
    <Space wrap size="middle" align="center" style={{ width: '100%' }}>
      <Upload
        multiple
        fileList={uploadFileList}
        beforeUpload={() => false}
        onChange={handleUploadChange}
        disabled={submitting}
        styles={workspaceUploadStyles}
        showUploadList={uploadFileList.length ? { showRemoveIcon: true } : false}
      >
        <Button icon={<PaperClipOutlined />} disabled={submitting}>
          Attach files
        </Button>
      </Upload>
      {files.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {files.length} file{files.length !== 1 ? 's' : ''} selected
        </Text>
      )}
    </Space>
  ) : null;

  return (
    <div style={{ borderTop: '1px solid var(--varexe-border-color)', paddingTop: 'var(--ant-padding-sm)' }}>
      <Space orientation="vertical" size="small" style={{ width: '100%' }}>
        {structuredModal}
        {canComment && (
          <Input.TextArea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a message…"
            disabled={submitting}
          />
        )}
        {uploadControl}
        <Space style={{ justifyContent: 'flex-end', width: '100%' }} wrap>
          {showRequests && (
            <Dropdown
              menu={{ items: PORTAL_MENU, onClick: ({ key }) => openActionModal(key) }}
              trigger={['click']}
            >
              <Button icon={<PlusOutlined />} disabled={submitting}>
                Request
              </Button>
            </Dropdown>
          )}
          {(canComment || canUpload) && (
            <Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={() => void handleSubmit()}>
              Send
            </Button>
          )}
        </Space>
      </Space>
    </div>
  );
});

WorkspaceEventComposer.displayName = 'WorkspaceEventComposer';

export default WorkspaceEventComposer;
